import type { ReactNode } from 'react';
import { Activity, ArrowLeftRight, BarChart3, Coins, Droplets, Layers, Lock, Users } from 'lucide-react';
import type { NavView } from './Header';
import type { TxType } from '../web3/logTransaction';
import { useTransactionStats } from '../web3/useTransactionStats';
import { usePairMetrics } from '../web3/usePairMetrics';
import { useProtocolStats } from '../web3/useProtocolStats';
import { shortenAddress } from '../web3/useWallet';
import MarketTickerTape from './MarketTickerTape';
import RwaSpotlight from './RwaSpotlight';
import TopPoolsTable from './TopPoolsTable';
import TrustBar from './TrustBar';

const TYPE_LABEL: Record<TxType, string> = {
  swap: 'Swap',
  addLiquidity: 'Add Liquidity',
  removeLiquidity: 'Remove Liquidity',
  stake: 'Stake',
  unstake: 'Unstake',
  harvest: 'Harvest',
  farmDeposit: 'Farm Deposit',
  farmWithdraw: 'Farm Withdraw',
  earnDeposit: 'Earn Deposit',
  earnWithdraw: 'Earn Withdraw',
  rwaBuy: 'RWA Buy',
  rwaSell: 'RWA Sell',
  rwaRedeem: 'RWA Redeem',
  launchpadBuy: 'Launchpad Buy',
  lendDeposit: 'Collateral Deposit',
  lendWithdraw: 'Collateral Withdraw',
  borrow: 'Borrow',
  repay: 'Repay',
  predictionBet: 'Prediction Bet',
  predictionClaim: 'Prediction Claim',
  bridgeLock: 'Bridge Lock',
  bridgeUnlock: 'Bridge Unlock',
};

const VOLUME_SOURCE_LABEL = {
  subgraph: 'Indexed · 24h',
  local: 'Session estimate · 24h',
  none: 'Awaiting indexer',
} as const;

function compactUsd(v: number | undefined) {
  if (v === undefined) return undefined;
  return `$${Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 2 }).format(v)}`;
}

export default function DashboardView({ onNavigate }: { onNavigate: (view: NavView) => void }) {
  const stats = useTransactionStats();
  const { metrics, loading: metricsLoading } = usePairMetrics();
  const protocol = useProtocolStats(metrics, metricsLoading);

  return (
    <div className="flex-1 overflow-y-auto p-6 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-semibold text-foam-100">Overview</h2>
          <p className="text-xs text-foam-500 mt-1">
            Live protocol metrics — TVL from on-chain reserves, volume &amp; fees from the indexer.
          </p>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-tide-400 px-2.5 py-1 rounded-full bg-tide-400/10">
          <span className="w-1.5 h-1.5 rounded-full bg-tide-400 animate-pulse" />
          Live
        </div>
      </div>

      <MarketTickerTape metrics={metrics} loading={metricsLoading} />

      {/* Hero protocol KPIs — the $-denominated headline metrics a DeFi exchange leads with */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard
          icon={Lock}
          label="Total Value Locked"
          value={compactUsd(protocol.tvlUsd)}
          caption="On-chain reserves"
          loading={protocol.loading}
          accent
        />
        <KpiCard
          icon={BarChart3}
          label="24h Volume"
          value={compactUsd(protocol.volume24hUsd)}
          caption={VOLUME_SOURCE_LABEL[protocol.volumeSource]}
          loading={protocol.loading}
        />
        <KpiCard
          icon={Coins}
          label="24h Fees"
          value={compactUsd(protocol.fees24hUsd)}
          caption="0.30% swap fee"
          loading={protocol.loading}
        />
        <KpiCard
          icon={Layers}
          label="Active Pools"
          value={protocol.loading ? undefined : `${protocol.activePairs}/${protocol.totalPairs}`}
          caption="With live liquidity"
          loading={protocol.loading}
        />
      </div>

      {/* RWA is the flagship of Pelagos — spotlight it directly under the KPIs */}
      <RwaSpotlight onNavigate={onNavigate} />

      <TopPoolsTable metrics={metrics} loading={metricsLoading} onNavigate={onNavigate} />

      {/* Secondary protocol activity — real transactions logged from our own users */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <MiniStat icon={Activity} label="Total Transactions" value={stats.loading ? undefined : stats.totalCount} />
        <MiniStat icon={Users} label="Unique Wallets" value={stats.loading ? undefined : stats.uniqueUsers} />
        <MiniStat
          icon={ArrowLeftRight}
          label="Swaps"
          value={stats.loading ? undefined : stats.byType.get('swap') ?? 0}
        />
        <MiniStat
          icon={Droplets}
          label="Liquidity Adds"
          value={stats.loading ? undefined : stats.byType.get('addLiquidity') ?? 0}
        />
      </div>

      <Panel title="Recent Activity">
        {stats.loading ? (
          <Loading />
        ) : stats.recent.length === 0 ? (
          <EmptyState
            message="아직 온체인 활동이 기록되지 않았습니다."
            cta="첫 스왑 실행하기"
            onClick={() => onNavigate('Swap')}
          />
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="text-foam-500 text-left">
                <th className="font-medium pb-2">Type</th>
                <th className="font-medium pb-2">Pool</th>
                <th className="font-medium pb-2">Wallet</th>
                <th className="font-medium pb-2 text-right">Time</th>
              </tr>
            </thead>
            <tbody>
              {stats.recent.map((tx) => (
                <tr key={tx.hash} className="border-t border-abyss-700">
                  <td className="py-2 text-tide-400 font-medium">{TYPE_LABEL[tx.type]}</td>
                  <td className="py-2 text-foam-100">{tx.pool}</td>
                  <td className="py-2 text-foam-300">{shortenAddress(tx.account)}</td>
                  <td className="py-2 text-right text-foam-500">
                    {tx.at ? new Date(tx.at).toLocaleString() : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>

      <TrustBar />
    </div>
  );
}

function KpiCard({
  icon: Icon,
  label,
  value,
  caption,
  loading,
  accent = false,
}: {
  icon: typeof Activity;
  label: string;
  value: string | undefined;
  caption: string;
  loading: boolean;
  accent?: boolean;
}) {
  return (
    <div
      className={`p-4 rounded-xl border bg-abyss-850 ${
        accent ? 'border-tide-500/30 bg-gradient-to-br from-tide-500/[0.06] to-transparent' : 'border-abyss-700'
      }`}
    >
      <div className="flex items-center gap-1.5 text-xs text-foam-500">
        <Icon size={13} className={accent ? 'text-tide-400' : ''} />
        {label}
      </div>
      <div className="text-2xl font-semibold text-foam-100 mt-2 tabular-nums">
        {loading ? (
          <span className="inline-block w-20 h-7 rounded bg-abyss-700 animate-pulse" />
        ) : (
          value ?? <span className="text-foam-500">—</span>
        )}
      </div>
      <div className="text-[11px] text-foam-500 mt-1.5">{caption}</div>
    </div>
  );
}

function MiniStat({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof Activity;
  label: string;
  value: number | undefined;
}) {
  return (
    <div className="p-3 rounded-xl border border-abyss-700 bg-abyss-850">
      <div className="flex items-center gap-1.5 text-[11px] text-foam-500">
        <Icon size={12} />
        {label}
      </div>
      <div className="text-lg font-semibold text-foam-100 mt-1 tabular-nums">
        {value === undefined ? (
          <span className="inline-block w-8 h-5 rounded bg-abyss-700 animate-pulse" />
        ) : (
          value.toLocaleString('en-US')
        )}
      </div>
    </div>
  );
}

function Panel({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="p-4 rounded-xl border border-abyss-700 bg-abyss-850">
      <h3 className="text-sm font-semibold text-foam-100 mb-3">{title}</h3>
      {children}
    </div>
  );
}

function Loading() {
  return (
    <div className="space-y-2 py-1">
      {[0, 1, 2].map((i) => (
        <div key={i} className="h-6 rounded bg-abyss-700/60 animate-pulse" />
      ))}
    </div>
  );
}

function EmptyState({ message, cta, onClick }: { message: string; cta: string; onClick: () => void }) {
  return (
    <div className="py-6 text-center space-y-2">
      <p className="text-xs text-foam-500">{message}</p>
      <button
        onClick={onClick}
        className="text-xs font-semibold text-tide-400 hover:text-tide-300 underline underline-offset-2"
      >
        {cta}
      </button>
    </div>
  );
}
